import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Siren, UserCircle, Map as MapIcon } from "lucide-react";
import { useT } from "../lib/lang";

// Each tab gets its own brand color so the bar reads as a deliberate palette
// rather than a row of grey labels. Active state lifts the icon, inks the
// label, and reveals a thick top accent bar.
//
// Iter74 — replaced the "Contacts" tab with a broader "Profil" tab that
// aggregates the citizen's emergency medical info AND the historic
// emergency-contact list. Route: `/profil`. The legacy `/contacts` route
// still resolves to the full contacts page (kept for any deep links),
// but is no longer surfaced in the bottom nav.
const NAV_BUILD = (t) => [
  { to: "/",         label: t.navSos,      short: "SOS",        icon: Siren,       testid: "nav-sos",      end: true,  color: "#7F1D1D", bg: "bg-red-50",     ring: "ring-red-500" },
  { to: "/profil",   label: "Profil",      short: "Profil",     icon: UserCircle,  testid: "nav-profil",               color: "#0EA5E9", bg: "bg-sky-50",     ring: "ring-sky-500" },
  { to: "/carte",    label: t.navCarte,    short: t.navCarte,   icon: MapIcon,     testid: "nav-carte",                color: "#0F766E", bg: "bg-teal-50",    ring: "ring-teal-500" },
];

export const BottomNav = () => {
  const [t] = useT();
  const { pathname } = useLocation();
  // The bottom-nav is a citizen-facing affordance (SOS / Contacts /
  // Carte). It must NEVER appear inside the Admin Dashboard or the
  // Agency Dashboard, which have their own admin-specific navigation
  // (top tab-bar on desktop, in-page menu on mobile). Hiding here keeps
  // App.js routing simple — one global mount, route-aware visibility.
  //
  // Admin shortcut intentionally NOT rendered here — the citizen app
  // must never expose an Admin entry-point in its primary nav, even
  // when an admin token is present on the same device. Admins reach
  // the dashboard via the direct /admin URL.
  if (pathname.startsWith("/admin")) return null;
  if (pathname.startsWith("/agence/")) return null;
  const items = NAV_BUILD(t);

  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t-2 border-rescue-line flex items-stretch z-50 px-1"
      style={{ height: "calc(72px + env(safe-area-inset-bottom))", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="bottom-nav"
    >
      {items.map(({ to, label, short, icon: Icon, testid, end, color, bg }) => (
        <NavLink
          key={to}
          to={to}
          end={!!end}
          data-testid={testid}
          className={({ isActive }) =>
            `relative flex-1 flex flex-col items-center justify-center gap-0.5 px-0.5 transition-all ${
              isActive ? `${bg}` : "hover:bg-neutral-50"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <span
                aria-hidden
                className={`absolute top-0 left-1/2 -translate-x-1/2 h-1 transition-all duration-300 ${isActive ? "w-8" : "w-0"}`}
                style={{ background: color }}
              />
              <span
                className={`flex items-center justify-center w-9 h-9 rounded-full transition-all ${isActive ? "scale-110" : ""}`}
                style={isActive ? { background: color, color: "#ffffff" } : { color: "#525252" }}
              >
                <Icon strokeWidth={2.5} className="w-[18px] h-[18px]" />
              </span>
              <span
                className="text-[9px] font-bold uppercase tracking-[0.18em] leading-none truncate max-w-full"
                style={{ color: isActive ? color : "#737373" }}
              >
                {short}
              </span>
              <span className="sr-only">{label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
};
